import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../button';
import { Input } from '../input';
import './multi-select-dropdown.scss';

const defaultGetOptionLabel = (option) => option?.title ?? option?.label ?? '';
const defaultGetOptionValue = (option) => option?.value ?? option?.id ?? option;

const MultiSelectDropdown = ({
  label = 'Filter',
  options = [],
  selected = [],
  onApply,
  onChange,
  applyOnChange = false,
  rightIcon = null,
  placeholder = 'Search',
  applyText = 'Apply filter',
  showApplyButton = true,
  stickyFooter = false,
  showCount = true,
  searchEnabled = true,
  menuWidth = 260,
  menuMaxHeight = 300,
  triggerStyle = {},
  menuStyle = {},
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  renderOption = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState(selected);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setTempSelected(selected);
    }
  }, [selected, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCount = selected?.length || 0;
  const labelText = showCount ? label : label;

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
    setSearchText('');
    setTempSelected(selected);
  };

  const handleToggleOption = (option) => {
    const value = getOptionValue(option);
    const nextSelected = tempSelected.some((item) => getOptionValue(item) === value)
      ? tempSelected.filter((item) => getOptionValue(item) !== value)
      : [...tempSelected, option];
    setTempSelected(nextSelected);
    if (applyOnChange) {
      onApply?.(nextSelected);
      onChange?.(nextSelected);
    }
  };

  const applySelection = () => {
    onApply?.(tempSelected);
    onChange?.(tempSelected);
    setIsOpen(false);
  };

  const filteredOptions = useMemo(() => {
    if (!searchEnabled || !searchText) {
      return options;
    }
    const lowered = searchText.toLowerCase();
    return options.filter((option) =>
      String(getOptionLabel(option)).toLowerCase().includes(lowered)
    );
  }, [options, searchText, searchEnabled, getOptionLabel]);

  return (
    <div ref={dropdownRef} className="multi-select-dropdown">
      <button
        type="button"
        onClick={toggleMenu}
        className="multi-select-dropdown__button"
        style={{
          minWidth: 140,
          ...triggerStyle
        }}
      >
        <span style={{ fontFamily: 'Montserrat Medium' }}>
          {labelText}
          {showCount && selectedCount > 0 && (
            <span className=""> : +{selectedCount}</span>
          )}
        </span>
        {rightIcon && (
          <span
            style={{
              color: '#0095FF',
            }}
          >
            {typeof rightIcon === 'function' ? rightIcon({ isOpen }) : rightIcon}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          className="multi-select-dropdown__menu"
          style={{
            width: menuWidth,
            maxHeight: menuMaxHeight,
            ...(stickyFooter
              ? { display: 'flex', flexDirection: 'column', paddingBottom: 0 }
              : {}),
            ...menuStyle
          }}
        >
          {searchEnabled && (
            <div className="multi-select-dropdown__search">
              <Input
                value={searchText}
                placeholder={placeholder}
                onChange={setSearchText}
                style={{
                  height: 32,
                  padding: '6px 8px',
                  borderRadius: 4,
                  border: '1px solid #ddd'
                }}
              />
            </div>
          )}
          <div
            className="multi-select-dropdown__options"
            style={stickyFooter ? { flex: 1, overflowY: 'auto' } : undefined}
          >
            {filteredOptions.map((option) => {
              const value = getOptionValue(option);
              const checked = tempSelected.some(
                (item) => getOptionValue(item) === value
              );
              return (
                <label
                  key={String(value)}
                  className="multi-select-dropdown__option"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleOption(option)}
                  />
                  {renderOption
                    ? renderOption({ option, checked })
                    : getOptionLabel(option)}
                </label>
              );
            })}
          </div>
          {showApplyButton && (
            <div
              className="multi-select-dropdown__footer"
              style={stickyFooter ? { position: 'sticky', bottom: 0, background: '#fff' } : undefined}
            >
              <Button
                quaternary
                text={applyText}
                style={{ width: '100%', borderRadius: 8, height: 36 }}
                onClick={applySelection}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { MultiSelectDropdown };
